<template>
    <div v-if="isNew" class="machine-body">
        <Message v-if="writeFailure" severity="error" :closable="false" class="machine-body__refusal">
            {{ writeFailure }}
        </Message>
        <section class="machine-body__section">
            <div class="machine-body__label">
                <span>Name</span>
                <small>What this program is called. Saving makes its first credential and shows the secret once.</small>
            </div>
            <InputText
                :model-value="draft?.name ?? ''"
                data-field="name"
                @update:model-value="(value) => setField('name', value)"
            />
        </section>
    </div>

    <div v-else-if="record" class="machine-body">
        <section v-for="field in RECORD_FIELDS" :key="field" class="machine-body__section">
            <div class="machine-body__label">
                <span>{{ fieldMeta[field]?.label ?? field }}</span>
            </div>
            <FormatValue
                class="machine-body__value"
                :record="record"
                :field-key="field"
                :field-definitions="fieldDefinitions"
                :field-meta="fieldMeta"
            />
        </section>

        <section class="machine-body__section">
            <div class="machine-body__label">
                <span>Machine ID</span>
            </div>
            <code class="machine-body__code">{{ record.id }}</code>
        </section>

        <section class="machine-body__lifecycle">
            <div class="machine-body__label">
                <span>Actions</span>
                <small>A suspended program's tokens stop at once. A retired one is kept for history and never comes back.</small>
            </div>
            <div class="machine-body__actions">
                <Button
                    v-if="record.status === 'active'"
                    label="Suspend"
                    icon="pi pi-pause"
                    severity="warn"
                    variant="outlined"
                    size="small"
                    :disabled="busy"
                    data-action="suspend"
                    @click="askSuspend"
                />
                <Button
                    v-if="record.status === 'suspended'"
                    label="Resume"
                    icon="pi pi-play"
                    severity="secondary"
                    variant="outlined"
                    size="small"
                    :disabled="busy"
                    data-action="resume"
                    @click="resume"
                />
                <Button
                    v-if="record.status !== 'retired'"
                    label="Rotate credential"
                    icon="pi pi-refresh"
                    severity="secondary"
                    variant="outlined"
                    size="small"
                    :disabled="busy"
                    data-action="rotate"
                    @click="rotating = true"
                />
                <Button
                    v-if="record.status !== 'retired'"
                    label="Retire"
                    icon="pi pi-ban"
                    severity="danger"
                    variant="outlined"
                    size="small"
                    :disabled="busy"
                    data-action="retire"
                    @click="askRetire"
                />
            </div>
            <p v-if="failure" class="machine-body__failure">{{ failure }}</p>
        </section>

        <section class="machine-body__section">
            <div class="machine-body__label">
                <span>Credentials</span>
                <small>A client id and its secret. Rotating adds a second beside the first; the first keeps working for the hours you choose (24 unless you say otherwise), then it is revoked.</small>
            </div>
            <ul class="machine-body__list" data-list="credentials">
                <li v-for="credential in record.credentials" :key="credential.clientId" class="machine-body__row" :data-credential="credential.clientId">
                    <i class="pi pi-key" />
                    <div class="machine-body__row-body">
                        <code>{{ credential.clientId }}</code>
                        <small v-if="credential.connectedAt">Since {{ when(credential.connectedAt) }}</small>
                        <small v-if="credential.dormantAt">Revoked {{ when(credential.dormantAt) }}</small>
                        <small v-else-if="credential.expiresAt" data-expiry>Expires {{ when(credential.expiresAt) }}</small>
                    </div>
                    <Tag v-if="credential.dormantAt" value="Revoked" severity="secondary" />
                    <Tag v-else-if="isExpired(credential)" value="Expired" severity="warn" />
                    <Button
                        v-else-if="record.status !== 'retired'"
                        label="Revoke credential"
                        severity="danger"
                        variant="outlined"
                        size="small"
                        :disabled="busy"
                        data-action="revoke-credential"
                        @click="askRevokeCredential(credential)"
                    />
                </li>
            </ul>
            <p v-if="record.credentials.length === 0" class="machine-body__empty">No credentials.</p>
        </section>

        <section class="machine-body__section">
            <div class="machine-body__label">
                <span>Live tokens</span>
                <small>Every token this program holds that has not expired, and where and when each was last used.</small>
            </div>
            <ProgressSpinner v-if="detail === null && store.loading" class="machine-body__loading" style="width: 24px; height: 24px" />
            <p v-else-if="detail === null && loadFailure" class="machine-body__failure">{{ loadFailure }}</p>
            <template v-else-if="detail">
                <ul class="machine-body__list" data-list="tokens">
                    <li v-for="token in tokenRows" :key="token.tokenId" class="machine-body__row" :data-token="token.tokenId">
                        <i class="pi pi-ticket" />
                        <div class="machine-body__row-body">
                            <code>{{ token.tokenId }}</code>
                            <small v-for="line in token.details" :key="line">{{ line }}</small>
                        </div>
                        <Button
                            v-if="record.status !== 'retired'"
                            label="Revoke token"
                            severity="danger"
                            variant="outlined"
                            size="small"
                            :disabled="busy"
                            data-action="revoke-token"
                            @click="askRevokeToken(token)"
                        />
                    </li>
                </ul>
                <p v-if="tokenRows.length === 0" class="machine-body__empty">No live tokens.</p>
                <!-- Retiring made every token stale already; a retired Machine offers no action. -->
                <Button
                    v-if="record.status !== 'retired'"
                    label="Revoke all tokens"
                    icon="pi pi-times"
                    severity="danger"
                    variant="outlined"
                    size="small"
                    class="machine-body__all-tokens"
                    :disabled="busy || tokenRows.length === 0"
                    data-action="revoke-all-tokens"
                    @click="askRevokeAllTokens"
                />
            </template>
        </section>

        <ActionConfirmDialog
            v-model:open="confirm.open"
            :title="confirm.title"
            :message="confirm.message"
            :confirm-label="confirm.confirmLabel"
            destructive
            :busy="busy"
            :error="confirm.error"
            @confirm="runConfirmed"
        />

        <RotateCredentialDialog
            v-model:open="rotating"
            :busy="busy"
            :error="rotateFailure"
            @rotate="rotate"
        />
    </div>
</template>

<script setup>
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Message from 'primevue/message'
import ProgressSpinner from 'primevue/progressspinner'
import Tag from 'primevue/tag'
import { computed, ref, watch } from 'vue'

import { FormatValue } from '@/admin-collection-layout/lib/formatValue.js'
import ActionConfirmDialog from '@/components/identity/ActionConfirmDialog.vue'
import RotateCredentialDialog from '@/components/identity/RotateCredentialDialog.vue'
import { isoFromSeconds } from '@/lib/identityCollections.js'

defineOptions({ name: 'MachineInstanceBody' })

// What the instance panel shows for a Machine: what it is, each credential, every live token
// with where it was used, and the actions — suspend or resume, rotate a credential, revoke a
// credential or a token or all of them, retire. Every destructive one runs through the confirm,
// which says what happens. A rotate answers a secret once; it is emitted to the view, whose
// dialog is the only place it is ever shown.
//
// A new Machine is a name box: saving makes it, and the view shows that secret the same way.
const props = defineProps({
    // The list row: `{ id, name, status, credentials, credentialCount }`.
    record: { type: Object, default: null },
    draft: { type: Object, default: null },
    isNew: { type: Boolean, default: false },
    // Writes one key into the panel's draft — the draft is what gets saved.
    setField: { type: Function, default: null },
    // `machinesStore()`.
    store: { type: Object, required: true },
    fieldDefinitions: { type: Object, required: true },
    fieldMeta: { type: Object, default: () => ({}) },
    // What the last create was refused with, or null.
    writeFailure: { type: String, default: null }
})

const emit = defineEmits(['issued'])

const RECORD_FIELDS = ['name', 'status']

const busy = ref(false)
const failure = ref(null)
const loadFailure = ref(null)
const confirm = ref({ open: false, title: '', message: '', confirmLabel: '', error: '', action: null })
// The rotate dialog: open while the administrator chooses the hours, with the last refusal.
const rotating = ref(false)
const rotateFailure = ref('')

// Past its expiry and not yet swept: the lookup refuses it already; the next read sweeps it.
function isExpired(credential) {
    if (!credential.expiresAt) {
        return false
    }
    return new Date(credential.expiresAt).getTime() <= Date.now()
}

// Which program the held detail is about: `store.current` outlives the record it was read for.
const detail = computed(function () {
    if (props.store.current === null || props.store.current.id !== props.record?.id) {
        return null
    }
    return props.store.current
})

async function loadDetail() {
    loadFailure.value = null
    if (props.record === null || props.isNew) {
        return
    }
    const ok = await props.store.load(props.record.id)
    if (ok === false) {
        loadFailure.value = props.store.error
    }
}

watch(() => props.record?.id, loadDetail, { immediate: true })

function when(iso) {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

// A token always carries `issuedAt` and `expiresAt`, and `lastUsedAt` is read only when a use
// was recorded, so there is always a number here.
function whenSeconds(seconds) {
    return when(isoFromSeconds(seconds))
}

// What a token row says: issued and expires, last use, uses by service, the secret that made
// it (by fingerprint) and the caller's address. A detail nobody recorded is left out. Read only
// under the template's `v-if="detail"`, so the detail is there when this runs.
const tokenRows = computed(() => detail.value.tokens.map(function (token) {
    const details = [`Issued ${whenSeconds(token.issuedAt)} · expires ${whenSeconds(token.expiresAt)}`]
    const use = token.use ?? {}
    if (use.lastUsedAt) {
        details.push(`Last used ${whenSeconds(use.lastUsedAt)} by ${use.lastUsedBy ?? 'an unknown service'}`)
    } else {
        details.push('Never used')
    }
    const byService = Object.entries(use.byService ?? {}).map(([service, count]) => `${service} ${count}`)
    if (byService.length > 0) {
        details.push(`Uses: ${byService.join(', ')}`)
    }
    details.push(`Credential ${token.clientId} · secret ${token.secretFingerprint}`)
    if (token.callerAddress) {
        details.push(`Caller ${token.callerAddress}`)
    }
    return { tokenId: token.tokenId, secretFingerprint: token.secretFingerprint, details }
}))

function name() {
    return props.record.name || 'this program'
}

function askSuspend() {
    confirm.value = {
        open: true,
        title: `Suspend ${name()}?`,
        message: `Every token ${name()} holds stops working at once, and it cannot get a new one until it is resumed. Its credentials are untouched.`,
        confirmLabel: 'Suspend',
        error: '',
        action: () => props.store.setStatus(props.record.id, 'suspended')
    }
}

function askRetire() {
    confirm.value = {
        open: true,
        title: `Retire ${name()}?`,
        message: `Every credential ${name()} holds is destroyed and every token stops working at once. The record is kept for history and cannot be brought back.`,
        confirmLabel: 'Retire',
        error: '',
        action: () => props.store.retire(props.record.id)
    }
}

function askRevokeCredential(credential) {
    confirm.value = {
        open: true,
        title: 'Revoke this credential?',
        message: `Credential ${credential.clientId} is deleted and every token it made stops working at once. The program must use another credential from then on.`,
        confirmLabel: 'Revoke credential',
        error: '',
        action: () => props.store.revokeCredential(props.record.id, credential.clientId)
    }
}

function askRevokeToken(token) {
    confirm.value = {
        open: true,
        title: 'Revoke this token?',
        message: `Token ${token.tokenId} stops working on every server at once. The program can trade its credential for a new one.`,
        confirmLabel: 'Revoke token',
        error: '',
        action: () => props.store.revokeTokens(props.record.id, { tokenId: token.tokenId })
    }
}

function askRevokeAllTokens() {
    confirm.value = {
        open: true,
        title: 'Revoke every token?',
        message: `Every token ${name()} holds stops working on every server at once. The program can trade its credential for a new one.`,
        confirmLabel: 'Revoke all tokens',
        error: '',
        action: () => props.store.revokeTokens(props.record.id)
    }
}

// A failure answers inside the open confirm, and confirming again retries. Success closes it
// and re-reads the page and the program, so the status, the credentials and the tokens are live.
async function runConfirmed() {
    busy.value = true
    confirm.value.error = ''
    const ok = await confirm.value.action()
    if (ok === false) {
        confirm.value.error = props.store.error
        busy.value = false
        return
    }
    await props.store.refresh()
    confirm.value.open = false
    busy.value = false
}

// Resuming takes nothing away, so it needs no confirm; its refusal reads beside the buttons.
async function resume() {
    await settle(() => props.store.setStatus(props.record.id, 'active'))
}

// Rotating adds a credential and gives the old one the hours the dialog asked; what it answers
// is the one secret, handed up to be shown once. A refusal stays inside the open dialog.
async function rotate(expiresInHours) {
    rotateFailure.value = ''
    const answer = await settle(() => props.store.rotateCredential(props.record.id, { expiresInHours }))
    if (answer === null || answer === false) {
        rotateFailure.value = props.store.error
        return
    }
    rotating.value = false
    emit('issued', answer)
}

async function settle(work) {
    busy.value = true
    failure.value = null
    const answer = await work()
    if (answer === false || answer === null) {
        failure.value = props.store.error
    } else {
        await props.store.refresh()
    }
    busy.value = false
    return answer
}
</script>

<style scoped>
.machine-body {
    display: flex;
    flex-direction: column;
}

.machine-body__section,
.machine-body__lifecycle {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 0;
}

.machine-body__label {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.machine-body__label span {
    font-size: 13px;
    font-weight: 600;
}

.machine-body__label small {
    color: var(--p-surface-400);
    font-size: 11px;
    line-height: 1.4;
}

.machine-body__value {
    font-size: 13px;
}

.machine-body__code {
    font-family: var(--p-font-mono);
    font-size: 12px;
    overflow-wrap: anywhere;
}

.machine-body__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.machine-body__refusal {
    margin: 0 0 12px;
}

.machine-body__failure {
    color: var(--p-status-danger);
    font-size: 12px;
    margin: 4px 0 0;
}

.machine-body__list {
    list-style: none;
    margin: 4px 0 0;
    padding: 0;
}

.machine-body__row {
    display: flex;
    align-items: center;
    gap: 10px;
    border-bottom: 1px solid var(--p-content-border-color);
    font-size: 13px;
    padding: 8px 0;
}

.machine-body__row > i {
    color: var(--p-primary-color);
    font-size: 14px;
}

.machine-body__row-body {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
}

.machine-body__row-body small {
    color: var(--p-surface-400);
    font-size: 11px;
    overflow-wrap: anywhere;
}

.machine-body__row-body code {
    font-family: var(--p-font-mono);
    font-size: 12px;
    overflow-wrap: anywhere;
}

.machine-body__empty {
    color: var(--p-surface-400);
    font-size: 12px;
    margin: 0;
}

.machine-body__loading {
    align-self: flex-start;
}

.machine-body__all-tokens {
    align-self: flex-start;
    margin-top: 8px;
}
</style>
